import { useState, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { formatResetTime } from '../utils/timeFormatter';

/**
 * Hook to check budget status on mount.
 * Supports both legacy (single window) and new (multi-window) response formats.
 *
 * @param {string} userId
 * @param {boolean} isAuth
 * @returns {{ limitInfo, budgetStatus, checkUsageStatus }}
 */
export function useUsageLimit(userId, isAuth) {
    const [limitInfo, setLimitInfo] = useState(null);
    const [budgetStatus, setBudgetStatus] = useState(null);

    const checkUsageStatus = async () => {
        try {
            const usage = await apiClient.getUsageStatus();
            if (!usage) return;

            setBudgetStatus(usage);

            const isAllowed = usage.is_allowed !== undefined
                ? usage.is_allowed
                : usage.is_within_limit;

            if (isAllowed === false) {
                const denied = usage.denied_by;
                let resetTime = null;

                if (denied && usage.windows) {
                    const win = usage.windows.find(w => w.type === denied.window);
                    if (win) {
                        resetTime = win.reset_time || (win.reset_at ? formatResetTime(win.reset_at) : null);
                    }
                } else if (usage.window?.reset_at) {
                    resetTime = formatResetTime(usage.window.reset_at);
                } else if (usage.reset_time) {
                    resetTime = usage.reset_time;
                }

                setLimitInfo({
                    message: usage.message || 'Bạn đã đạt giới hạn sử dụng',
                    resetTime,
                    deniedBy: denied || null,
                    tier: usage.tier,
                });
            } else {
                setLimitInfo(null);
            }
        } catch (error) {
            console.debug('Usage status check failed:', error);
        }
    };

    useEffect(() => {
        if (!isAuth || !userId) {
            setLimitInfo(null);
            setBudgetStatus(null);
            return;
        }
        checkUsageStatus();
    }, [userId, isAuth]);

    return { limitInfo, budgetStatus, checkUsageStatus };
}
